/**
 * Yoink Adventures V2 - Supabase Gateway
 *
 * THE CRITICAL FILE - Single point of access to Supabase.
 *
 * Rules:
 * - NO other file imports supabase directly
 * - NO other file creates a Supabase client
 * - ALL database/storage/auth calls go through this gateway
 *
 * Features:
 * - Single client instance
 * - Health check via pure fetch (bypasses frozen client)
 * - Auto-reinitialize when frozen state detected
 * - All exports are gated through health check
 */

import * as logger from './logger.js';
import * as diag from './auth-diagnostics.js';

// =============================================================================
// CLIENT MANAGEMENT
// =============================================================================

let client = null;

/**
 * Custom auth lock that executes immediately instead of using navigator.locks.
 *
 * Safari (especially "Add to Home Screen" web apps) can kill the page process
 * while it holds a Web Lock; the orphaned lock is never released, so every
 * subsequent client.auth call (getSession, refresh) deadlocks waiting for it.
 * This is the root cause of the long-standing "logged in but getCurrentUser()
 * returns null" bug on iPad Safari.
 *
 * This app is effectively single-tab on mobile, so cross-tab lock coordination
 * buys us nothing, and GoTrue's refresh-token reuse interval (~10s) tolerates
 * the rare concurrent refresh.
 */
async function noNavigatorLock(name, acquireTimeout, fn) {
  return await fn();
}

// NOTE: Removed automatic health checking (isHealthy, lastHealthCheck, HEALTH_CHECK_INTERVAL)
// The health check mechanism was recreating the Supabase client every 30 seconds
// which caused queries to hang. The Supabase SDK handles connection state internally.

/**
 * Initialize the Supabase client
 * Called once at app startup - client is never recreated
 */
function createClient() {
  if (!window.YOINK_CONFIG?.supabase?.url || !window.YOINK_CONFIG?.supabase?.anonKey) {
    logger.error('[Gateway] Missing Supabase configuration');
    return null;
  }

  if (!window.supabase?.createClient) {
    logger.error('[Gateway] Supabase SDK not loaded');
    return null;
  }

  // Keep config minimal - complex realtime config was causing query hangs
  client = window.supabase.createClient(
    window.YOINK_CONFIG.supabase.url,
    window.YOINK_CONFIG.supabase.anonKey,
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        lock: noNavigatorLock
      }
    }
  );

  return client;
}

// =============================================================================
// HEALTH CHECKING (Manual use only - for initial connection test)
// =============================================================================

/**
 * Get the localStorage key the Supabase SDK persists its session under
 * @returns {string|null}
 */
function getSessionStorageKey() {
  const url = window.YOINK_CONFIG?.supabase?.url;
  if (!url) return null;
  const projectId = new URL(url).hostname.split('.')[0];
  return `sb-${projectId}-auth-token`;
}

/**
 * Read the full persisted session directly from localStorage.
 *
 * This is the SDK-independent source of truth for "is the user logged in".
 * On iPad Safari the SDK's in-memory auth state can be broken (navigator.locks
 * deadlock, killed Home Screen process) while a perfectly valid session still
 * sits in storage — which is why raw REST calls kept working for "logged out"
 * users. Session recovery reads from here instead of trusting the SDK.
 *
 * @returns {object|null} The persisted session (access_token, refresh_token,
 *   expires_at, user, ...) or null if absent/corrupt
 */
export function getStoredSession() {
  try {
    const storageKey = getSessionStorageKey();
    if (!storageKey) return null;

    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    if (!parsed?.access_token) return null;
    return parsed;
  } catch (e) {
    logger.warn('[Gateway] Could not read session from storage:', e.message);
    return null;
  }
}

/**
 * Remove the persisted session from localStorage.
 *
 * Sign-out backstop: the SDK's signOut() goes through the same lock
 * machinery as getSession() and can hang on iPad Safari. Callers race
 * signOut() against a timeout and then clear storage with this, so
 * "Sign out" always works even when the SDK is wedged.
 */
export function clearStoredSession() {
  try {
    const storageKey = getSessionStorageKey();
    if (storageKey) localStorage.removeItem(storageKey);
  } catch (e) {
    logger.warn('[Gateway] Could not clear stored session:', e.message);
  }
}

/**
 * Decode the exp claim from a JWT without verifying it
 * @param {string} token
 * @returns {number|null} Expiry as epoch seconds, or null if undecodable
 */
function decodeJwtExp(token) {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json)?.exp || null;
  } catch {
    return null;
  }
}

// Treat tokens expiring within this margin as already expired, so a request
// doesn't go out with a token that dies in flight
const TOKEN_EXPIRY_MARGIN_SECONDS = 60;

/**
 * Check whether a stored session's access token is expired (or about to be)
 * @param {object} session - Session with expires_at and/or access_token
 * @returns {boolean}
 */
export function isSessionExpired(session) {
  const expiresAt = session?.expires_at ?? decodeJwtExp(session?.access_token);
  if (!expiresAt) return false; // Can't tell — let the server be the judge
  return (Date.now() / 1000) >= (expiresAt - TOKEN_EXPIRY_MARGIN_SECONDS);
}

// Dedupe concurrent refresh attempts — GoTrue rotates refresh tokens, so two
// parallel refreshes with the same token would invalidate the session
let refreshInFlight = null;

/**
 * Refresh the persisted session manually via the GoTrue REST endpoint,
 * bypassing the SDK entirely (the SDK's own refresh is subject to the same
 * lock deadlock as getSession).
 *
 * The fresh session is written back to the SDK's localStorage key in the
 * same shape, so both the SDK and the raw REST helpers pick it up.
 *
 * @param {number} timeoutMs - Timeout in milliseconds (default: 10000)
 * @returns {Promise<object|null>} The refreshed session, or null on failure
 */
export function refreshStoredSession(timeoutMs = 10000) {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = doRefreshStoredSession(timeoutMs).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefreshStoredSession(timeoutMs) {
  const stored = getStoredSession();
  if (!stored?.refresh_token) return null;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(
      `${window.YOINK_CONFIG.supabase.url}/auth/v1/token?grant_type=refresh_token`,
      {
        method: 'POST',
        headers: {
          'apikey': window.YOINK_CONFIG.supabase.anonKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: stored.refresh_token }),
        signal: controller.signal
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      logger.warn('[Gateway] Manual token refresh failed:', response.status, errorText);
      diag.record('refresh-failed', `HTTP ${response.status}`);
      return null;
    }

    const fresh = await response.json();
    if (!fresh?.access_token) return null;

    if (!fresh.expires_at && fresh.expires_in) {
      fresh.expires_at = Math.floor(Date.now() / 1000) + fresh.expires_in;
    }

    // Merge over the old session to preserve any extra keys the SDK stored
    const merged = { ...stored, ...fresh };
    const storageKey = getSessionStorageKey();
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(merged));
    }

    diag.record('token-refreshed', 'manual refresh via REST');
    return merged;
  } catch (err) {
    clearTimeout(timeoutId);
    logger.warn('[Gateway] Manual token refresh error:', err.message);
    diag.record('refresh-error', err.message);
    return null;
  }
}

/**
 * Get a non-expired access token for raw REST calls, refreshing if needed.
 * Falls back to null (anonymous) if no session or refresh fails — an anon
 * request hits RLS and fails loudly, which beats sending an expired JWT.
 * @returns {Promise<string|null>}
 */
async function getValidAccessToken() {
  const session = getStoredSession();
  if (!session?.access_token) return null;
  if (!isSessionExpired(session)) return session.access_token;

  diag.record('token-expired', 'attempting manual refresh');
  const refreshed = await refreshStoredSession();
  return refreshed?.access_token || null;
}

/**
 * Get auth token directly from localStorage (no expiry check)
 * Used by checkHealth for authorization header
 */
function getTokenFromStorage() {
  return getStoredSession()?.access_token || null;
}

/**
 * Build headers for raw REST calls, with a valid (refreshed if necessary)
 * auth token when one is available
 * @param {object} extraHeaders - Additional headers to merge in
 * @returns {Promise<object>}
 */
async function getAuthHeaders(extraHeaders = {}) {
  const headers = {
    'apikey': window.YOINK_CONFIG.supabase.anonKey,
    'Content-Type': 'application/json',
    ...extraHeaders
  };

  const token = await getValidAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Check if Supabase connection is healthy
 * Used once at app startup to verify connection, NOT automatically during operation
 * Uses pure fetch with AbortController for reliability
 * @param {number} timeoutMs - Timeout in milliseconds (default: 3000)
 * @returns {Promise<boolean>} True if connection is healthy
 */
export async function checkHealth(timeoutMs = 3000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const token = getTokenFromStorage();
    const url = `${window.YOINK_CONFIG.supabase.url}/rest/v1/categories?select=id&limit=1`;

    const headers = {
      'apikey': window.YOINK_CONFIG.supabase.anonKey,
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      return true;
    }

    logger.warn('[Gateway] Health check failed with status:', response.status);
    return false;

  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      logger.warn('[Gateway] Health check timed out after', timeoutMs, 'ms');
    } else {
      logger.warn('[Gateway] Health check error:', err.message);
    }
    return false;
  }
}

/**
 * Mark the connection as unhealthy (no-op)
 * Kept for API compatibility but does nothing since we removed health checking
 */
export function markUnhealthy() {
  // No-op - health checking disabled
}

/**
 * Ensure client is initialized
 * NOTE: Removed automatic health checking and client recreation as it was
 * causing issues where queries would hang after 30 seconds.
 * The Supabase client handles its own connection state.
 * @returns {Promise<boolean>} True if client exists
 */
async function ensureHealthy() {
  // Just check that client exists - don't do automatic health checks
  // The health check/recreation mechanism was causing more problems than it solved
  if (!client) {
    logger.warn('[Gateway] Client not initialized');
    return false;
  }
  return true;
}

// =============================================================================
// GATED DATABASE ACCESS
// =============================================================================

/**
 * Execute a raw fetch query to Supabase REST API
 * PREFERRED METHOD - bypasses SDK internal state issues when authenticated
 *
 * @param {string} table - Table name
 * @param {object} options - Query options
 * @param {string} options.select - Select columns (default: '*')
 * @param {object} options.filters - PostgREST filters as key-value pairs
 * @param {string} options.order - Order by column (e.g., 'created_at.desc')
 * @param {number} options.limit - Limit results
 * @param {number} options.offset - Offset for pagination
 * @param {number} options.timeout - Timeout in ms (default: 15000)
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 *
 * @example
 * // Simple query
 * const { data, error } = await gateway.rawQuery(TABLES.CATEGORIES, { select: 'id,name,emoji' });
 *
 * // With filters
 * const { data, error } = await gateway.rawQuery(TABLES.YOINKS, {
 *   select: '*,public_profiles(username)',
 *   filters: { category_id: 'eq.abc123' },
 *   order: 'created_at.desc',
 *   limit: 50
 * });
 */
export async function rawQuery(table, options = {}) {
  const {
    select = '*',
    filters = {},
    order = null,
    limit = null,
    offset = null,
    timeout = 15000
  } = options;

  // Build URL
  let url = `${window.YOINK_CONFIG.supabase.url}/rest/v1/${table}?select=${encodeURIComponent(select)}`;

  // Add filters
  for (const [key, value] of Object.entries(filters)) {
    url += `&${key}=${encodeURIComponent(value)}`;
  }

  // Add order
  if (order) {
    url += `&order=${encodeURIComponent(order)}`;
  }

  // Add pagination
  if (limit !== null) {
    url += `&limit=${limit}`;
  }
  if (offset !== null) {
    url += `&offset=${offset}`;
  }

  // Build headers (refreshes the auth token first if it has expired)
  const headers = await getAuthHeaders();

  // Execute with timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      return { data: null, error: new Error(`HTTP ${response.status}: ${errorText}`) };
    }

    const data = await response.json();
    return { data, error: null };

  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      return { data: null, error: new Error(`Query timed out after ${timeout}ms`) };
    }
    return { data: null, error: err };
  }
}

/**
 * Raw update function using direct REST API to avoid SDK hang issues.
 * Similar pattern to rawQuery but for PATCH operations.
 *
 * @param {string} table - Table name
 * @param {object} options - Query options
 * @param {object} options.filters - Filter conditions (e.g., { id: 'eq.abc123' })
 * @param {object} options.body - Data to update
 * @param {number} options.timeout - Timeout in ms (default 15000)
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 *
 * @example
 * const { data, error } = await gateway.rawUpdate(TABLES.PROFILES, {
 *   filters: { id: 'eq.user-uuid' },
 *   body: { full_name: 'New Name', bio: 'Updated bio' }
 * });
 */
export async function rawUpdate(table, options = {}) {
  const {
    filters = {},
    body = {},
    timeout = 15000
  } = options;

  // Build URL with filters
  let url = `${window.YOINK_CONFIG.supabase.url}/rest/v1/${table}`;

  // Add filters as query params
  const filterParams = Object.entries(filters)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
  if (filterParams) {
    url += `?${filterParams}`;
  }

  // Build headers (refreshes the auth token first if it has expired)
  const headers = await getAuthHeaders({ 'Prefer': 'return=representation' });

  // Execute with timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('[Gateway] rawUpdate error:', response.status, errorText);
      return { data: null, error: new Error(`HTTP ${response.status}: ${errorText}`) };
    }

    const data = await response.json();
    return { data, error: null };

  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      return { data: null, error: new Error(`Update timed out after ${timeout}ms`) };
    }
    logger.error('[Gateway] rawUpdate exception:', err);
    return { data: null, error: err };
  }
}

/**
 * Raw delete function using direct REST API to avoid SDK hang issues.
 * Similar pattern to rawQuery but for DELETE operations.
 *
 * @param {string} table - Table name
 * @param {object} options - Query options
 * @param {object} options.filters - Filter conditions (e.g., { id: 'eq.abc123' })
 * @param {number} options.timeout - Timeout in ms (default 15000)
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 *
 * @example
 * const { data, error } = await gateway.rawDelete(TABLES.YOINKS, {
 *   filters: { id: 'eq.yoink-uuid' }
 * });
 */
export async function rawDelete(table, options = {}) {
  const {
    filters = {},
    timeout = 15000
  } = options;

  // Build URL with filters
  let url = `${window.YOINK_CONFIG.supabase.url}/rest/v1/${table}`;

  // Add filters as query params
  const filterParams = Object.entries(filters)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
  if (filterParams) {
    url += `?${filterParams}`;
  }

  // Build headers (refreshes the auth token first if it has expired)
  const headers = await getAuthHeaders({ 'Prefer': 'return=representation' });

  // Execute with timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('[Gateway] rawDelete error:', response.status, errorText);
      return { data: null, error: new Error(`HTTP ${response.status}: ${errorText}`) };
    }

    const data = await response.json();
    return { data, error: null };

  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      return { data: null, error: new Error(`Delete timed out after ${timeout}ms`) };
    }
    logger.error('[Gateway] rawDelete exception:', err);
    return { data: null, error: err };
  }
}

/**
 * Insert one or more rows into a table (using raw fetch to avoid SDK hang issues)
 * @param {string} table - Table name
 * @param {Object} options - Insert options
 * @param {Object|Array} options.body - Row(s) to insert
 * @param {number} options.timeout - Timeout in ms (default 15000)
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export async function rawInsert(table, options = {}) {
  const {
    body = {},
    timeout = 15000
  } = options;

  const url = `${window.YOINK_CONFIG.supabase.url}/rest/v1/${table}`;

  // Build headers (refreshes the auth token first if it has expired)
  const headers = await getAuthHeaders({ 'Prefer': 'return=representation' });

  // Execute with timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('[Gateway] rawInsert error:', response.status, errorText);
      return { data: null, error: new Error(`HTTP ${response.status}: ${errorText}`) };
    }

    const data = await response.json();
    return { data, error: null };

  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      return { data: null, error: new Error(`Insert timed out after ${timeout}ms`) };
    }
    logger.error('[Gateway] rawInsert exception:', err);
    return { data: null, error: err };
  }
}

/**
 * Call a database function via the REST endpoint directly (no SDK client),
 * with the same expiry-aware auth headers as rawQuery. Functions that
 * return `setof <table>` support PostgREST resource embedding through the
 * select option, exactly like rawQuery.
 *
 * @param {string} fnName - Function name
 * @param {object} options
 * @param {object} options.params - Function arguments (POST body)
 * @param {string} options.select - Embedding/columns for setof-returning functions
 * @param {number} options.timeout - Timeout in ms (default 15000)
 * @returns {Promise<{data: any, error: Error|null}>}
 */
export async function rawRpc(fnName, options = {}) {
  const { params = {}, select = null, timeout = 15000 } = options;

  let url = `${window.YOINK_CONFIG.supabase.url}/rest/v1/rpc/${fnName}`;
  if (select) {
    url += `?select=${encodeURIComponent(select)}`;
  }

  // Build headers (refreshes the auth token first if it has expired)
  const headers = await getAuthHeaders();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('[Gateway] rawRpc error:', response.status, errorText);
      return { data: null, error: new Error(`HTTP ${response.status}: ${errorText}`) };
    }

    // A void RPC (e.g. set_location_status) answers 204 with an EMPTY
    // body — response.json() would throw here and misreport the succeeded
    // call as a failure. (Invisible until 2026-07-12: the only void-RPC
    // caller was fire-and-forget, so the phantom error was swallowed.)
    const text = await response.text();
    let data = null;
    if (text) {
      try { data = JSON.parse(text); } catch { data = text; }
    }
    return { data, error: null };

  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      return { data: null, error: new Error(`RPC timed out after ${timeout}ms`) };
    }
    logger.error('[Gateway] rawRpc exception:', err);
    return { data: null, error: err };
  }
}

/**
 * Call a database RPC function
 * @param {string} fnName - Function name
 * @param {object} params - Function parameters
 * @returns {Promise<PostgrestResponse>} RPC response
 */
export async function rpc(fnName, params = {}) {
  const healthy = await ensureHealthy();
  if (!healthy) {
    throw new Error('Database connection unavailable');
  }
  return client.rpc(fnName, params);
}

// =============================================================================
// GATED STORAGE ACCESS
// =============================================================================

/**
 * Get a gated reference to a storage bucket
 * @param {string} bucket - Bucket name
 * @returns {Promise<StorageFileApi>} Supabase storage API
 */
export async function storage(bucket) {
  const healthy = await ensureHealthy();
  if (!healthy) {
    throw new Error('Storage connection unavailable');
  }
  return client.storage.from(bucket);
}

// =============================================================================
// GATED AUTH METHODS
// =============================================================================

/**
 * Sign in with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise<AuthResponse>}
 */
export async function signIn(email, password) {
  const healthy = await ensureHealthy();
  if (!healthy) {
    return { data: null, error: new Error('Connection unavailable') };
  }
  return client.auth.signInWithPassword({ email, password });
}

/**
 * Sign up with email and password
 * @param {string} email
 * @param {string} password
 * @param {object} metadata - User metadata (username, full_name, etc.)
 * @returns {Promise<AuthResponse>}
 */
export async function signUp(email, password, metadata = {}) {
  const healthy = await ensureHealthy();
  if (!healthy) {
    return { data: null, error: new Error('Connection unavailable') };
  }
  return client.auth.signUp({
    email,
    password,
    options: { data: metadata }
  });
}

/**
 * Sign in with OAuth provider
 * @param {string} provider - Provider name ('google', etc.)
 * @param {string} redirectTo - Redirect URL after auth
 * @returns {Promise<AuthResponse>}
 */
export async function signInWithOAuth(provider, redirectTo) {
  const healthy = await ensureHealthy();
  if (!healthy) {
    return { data: null, error: new Error('Connection unavailable') };
  }
  return client.auth.signInWithOAuth({
    provider,
    options: { redirectTo }
  });
}

/**
 * Sign in with a provider-issued ID token (Google Identity Services).
 * Used by the in-page Google sign-in, which never leaves the app window —
 * the redirect flow's Custom Tab freezes on passkey verification inside
 * installed PWAs on Android (docs/AUTH-AUDIT-2026-08-21.md §1).
 * @param {string} provider - Provider name ('google')
 * @param {string} token - The ID token (JWT) issued by the provider
 * @param {string} [nonce] - Raw nonce whose SHA-256 hash was sent to the
 *   provider; omitted when the Web Crypto API is unavailable
 * @returns {Promise<AuthResponse>}
 */
export async function signInWithIdToken(provider, token, nonce) {
  const healthy = await ensureHealthy();
  if (!healthy) {
    return { data: null, error: new Error('Connection unavailable') };
  }
  return client.auth.signInWithIdToken({
    provider,
    token,
    ...(nonce ? { nonce } : {})
  });
}

/**
 * Sign out the current user
 * @returns {Promise<AuthResponse>}
 */
export async function signOut() {
  const healthy = await ensureHealthy();
  if (!healthy) {
    return { error: new Error('Connection unavailable') };
  }
  return client.auth.signOut();
}

/**
 * Get the current session
 *
 * Timeout-protected: The Supabase SDK's getSession() can hang indefinitely
 * when the internal lock mechanism (navigator.locks) deadlocks. This commonly
 * occurs in mobile "Add to Home Screen" apps where process lifecycle differs
 * from regular browser tabs — stale locks from killed processes may never
 * release, causing getSession() to wait forever.
 *
 * @param {number} timeoutMs - Timeout in milliseconds (default: 5000)
 * @returns {Promise<{data: {session: Session|null}, error: Error|null}>}
 */
export async function getSession(timeoutMs = 5000) {
  const healthy = await ensureHealthy();
  if (!healthy) {
    return { data: { session: null }, error: new Error('Connection unavailable') };
  }

  try {
    const result = await Promise.race([
      client.auth.getSession(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Session check timed out')), timeoutMs)
      )
    ]);

    if (result?.data?.session) {
      diag.record('getSession', 'SDK returned session');
      return result;
    }

    // SDK says no session, but its in-memory state can be broken on iPad
    // Safari while a valid persisted session exists — recover from storage
    const recovered = await recoverStoredSession();
    if (recovered) {
      return { data: { session: recovered }, error: null };
    }

    diag.record('getSession', 'no session (SDK + storage agree)');
    return result;
  } catch (err) {
    logger.warn('[Gateway] getSession failed:', err.message);
    diag.record('getSession-failed', err.message);

    // SDK hung or errored (navigator.locks deadlock) — recover from storage
    const recovered = await recoverStoredSession();
    if (recovered) {
      return { data: { session: recovered }, error: null };
    }

    return { data: { session: null }, error: err };
  }
}

/**
 * Recover a session from localStorage, refreshing it first if expired.
 * Used when the SDK's getSession() hangs or wrongly reports no session.
 * @returns {Promise<object|null>}
 */
async function recoverStoredSession() {
  const stored = getStoredSession();
  // A session is only recoverable if it carries the user object —
  // callers feed session.user straight into state.setCurrentUser()
  if (!stored?.user) return null;

  if (!isSessionExpired(stored)) {
    logger.info('[Gateway] Session recovered from storage (SDK bypass)');
    diag.record('session-recovered', 'valid session in storage');
    return stored;
  }

  const refreshed = await refreshStoredSession();
  if (refreshed) {
    logger.info('[Gateway] Session recovered via manual token refresh');
    diag.record('session-recovered', 'expired session refreshed manually');
  }
  return refreshed;
}

/**
 * Send password reset email
 * @param {string} email
 * @param {string} redirectTo - Redirect URL after reset
 * @returns {Promise<AuthResponse>}
 */
export async function resetPassword(email, redirectTo) {
  const healthy = await ensureHealthy();
  if (!healthy) {
    return { data: null, error: new Error('Connection unavailable') };
  }
  return client.auth.resetPasswordForEmail(email, { redirectTo });
}

/**
 * Update user password (after password reset)
 * @param {string} newPassword - The new password
 * @returns {Promise<AuthResponse>}
 */
export async function updatePassword(newPassword) {
  const healthy = await ensureHealthy();
  if (!healthy) {
    return { data: null, error: new Error('Connection unavailable') };
  }
  return client.auth.updateUser({ password: newPassword });
}

/**
 * Subscribe to auth state changes
 * @param {function} callback - Callback function (event, session)
 * @returns {Subscription} Unsubscribe function
 */
export function onAuthChange(callback) {
  if (!client) {
    logger.error('[Gateway] Cannot subscribe to auth changes - client not initialized');
    return { data: { subscription: { unsubscribe: () => {} } } };
  }

  return client.auth.onAuthStateChange(callback);
}

// =============================================================================
// UTILITY EXPORTS
// =============================================================================

/**
 * Get the storage URL for a file
 * @param {string} bucket - Bucket name
 * @param {string} path - File path
 * @returns {string} Public URL
 */
export function getPublicUrl(bucket, path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${window.YOINK_CONFIG.supabase.url}/storage/v1/object/public/${bucket}/${path}`;
}

/**
 * Upload a file to storage
 * @param {string} bucket - Bucket name
 * @param {string} path - File path
 * @param {File|Blob} file - File to upload
 * @param {Object} options - Upload options (upsert, contentType)
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function uploadFile(bucket, path, file, options = {}) {
  const healthy = await ensureHealthy();
  if (!healthy) {
    return { data: null, error: new Error('Storage connection unavailable') };
  }
  return client.storage.from(bucket).upload(path, file, {
    upsert: options.upsert !== false, // Default to upsert
    contentType: options.contentType || file.type || 'application/octet-stream'
  });
}

/**
 * Delete a file from storage
 * @param {string} bucket - Bucket name
 * @param {string} path - File path (or array of paths)
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export async function deleteFile(bucket, path) {
  const healthy = await ensureHealthy();
  if (!healthy) {
    return { data: null, error: new Error('Storage connection unavailable') };
  }
  const paths = Array.isArray(path) ? path : [path];
  return client.storage.from(bucket).remove(paths);
}

/**
 * Check if gateway is initialized
 * @returns {boolean}
 */
export function isInitialized() {
  return client !== null;
}

/**
 * Get current health status (without checking)
 * Always returns true if client exists since we removed automatic health tracking
 * @returns {boolean}
 */
export function getHealthStatus() {
  return client !== null;
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize the gateway
 * Call this once at app startup
 */
export function init() {
  if (client) {
    return true;
  }

  return createClient() !== null;
}

// Note: Don't auto-initialize here - let app.js call init() explicitly
// This ensures Supabase SDK has loaded first
